import {StandardDateRange} from "@/lib/queries/reports.ts";
import {
  createDefaultConfig,
  createSeriesRef,
  EXPLORER_CONFIG_PARAM,
  ExplorerConfig,
  serializeConfig,
} from "@/pages/metricsExplorer/explorerConfig.ts";

/**
 * The per-step transaction table.
 *
 * Named here rather than discovered, because it is the one data type the preset has to
 * pick deliberately: every other series lets the catalog choose the default for its check
 * type. The name is part of the backend's published metric catalog and does not change.
 */
const TRANSACTION_STEP_DATA_TYPE = "transaction_step";
const TRANSACTION_CHECK_TYPE = "TRANSACTION";

/**
 * The chart a check's analysis page opens the explorer with.
 *
 * Data type and metrics are left blank so the explorer fills them from the catalog for
 * whatever the check type turns out to be — the frontend does not keep its own copy of
 * which tables a check writes to.
 *
 * Transaction checks get a second series on the step table, split by step. Per-step timing
 * is the thing the explorer was built to show, and it should be on screen when the page
 * opens rather than something to assemble by hand.
 */
export const buildCheckPreset = (checkId: number, checkType: string): ExplorerConfig => {
  const base = createDefaultConfig();
  const overall = {...base.series[0], checkIds: [checkId], groupBy: ["location"]};
  if (checkType !== TRANSACTION_CHECK_TYPE) {
    return {...base, series: [overall]};
  }
  return {
    ...base,
    series: [
      {...overall, groupBy: []},
      {
        ref: createSeriesRef(),
        checkIds: [checkId],
        dataType: TRANSACTION_STEP_DATA_TYPE,
        metrics: [],
        aggregation: "AVG",
        groupBy: ["step_number"],
        filters: {},
        hidden: false,
      },
    ],
  };
};

/**
 * Explorer link for a check, carrying the date range the user is already looking at.
 *
 * A page that was never given a range has none in its URL and shows its own default, which
 * differs from the explorer's. `defaultRange` is that default, so the explorer opens on the
 * window the user was actually looking at.
 */
export const buildExplorerLink = (
  checkId: number,
  checkType: string,
  currentSearch: string,
  defaultRange: StandardDateRange
): string => {
  const params = new URLSearchParams(currentSearch);
  const carried = new URLSearchParams();
  ["start", "end", "range"].forEach((key) => {
    const value = params.get(key);
    if (value) {
      carried.set(key, value);
    }
  });
  if (carried.size === 0) {
    carried.set("range", defaultRange);
  }
  carried.set(EXPLORER_CONFIG_PARAM, serializeConfig(buildCheckPreset(checkId, checkType)));
  return `/metrics-explorer?${carried.toString()}`;
};
